import { useRef } from 'react';
import type { Dispatch, SetStateAction, MutableRefObject } from 'react';
import { Column, Columns, Inline, LinkButton, Stack, vars } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import ResultFiltersToggleButton from '../search-filters/v3/ResultFiltersToggleButton';
import { resetAllFilters } from '../search-filters/utils/searchFilters';
import type { Filters } from '../search-filters/v3/MobileFilters';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormNewContainer';
import type { NearbyDatePricesType } from '../../models';
import DesktopFilters from '../search-filters/v3/DesktopFilters';
import { useResultPageContext } from './contexts/ResultPageContext';
import ResultsSection from './ResultsSection';

const contentWrapperStyles = css`
  margin-top: 24px;
`;

const sortAndFilterControlsStyles = css`
  background: ${vars.colors.page.background.primary};
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  width: 100%;

  > button {
    max-width: 160px;
  }

  span {
    text-transform: capitalize;
  }
`;

const desktopFilterControlsWhenSidebarDisabled = css`
  align-items: center;
  border: none;
  border-radius: 0;
  display: flex;
  flex-grow: 1;
  font-size: 0.875rem;
  justify-content: space-between;
  padding: 8px;
  padding-left: 0;
`;

interface SearchResultsDesktopProps {
  isNearbyDatesLoading: boolean;
  nearbyDatePrices: NearbyDatePricesType;
  onFilter: (filter: any) => void;
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  selectedFilters: Filters;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
  sidebarRef: MutableRefObject<HTMLDivElement | null>;
}

const SearchResultsDesktop = ({
  isNearbyDatesLoading,
  nearbyDatePrices,
  onFilter,
  searchFormWatchRef,
  selectedFilters,
  setFetchMoreResults,
  setValueRef,
  sidebarRef,
}: SearchResultsDesktopProps) => {
  const { searchData, setIsItineraryDrawerSelfTransferModalOpen } = useResultPageContext();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const flightResultsWrapperRef = useRef(null);
  const quickSortVersion = p('Result.Page.QuickSort.Version');
  const isSortingInformationEnabled = p('Result.SortingInformation.Enabled');
  const isResultPageV3DesktopDrawerSearchResultFiltersEnabled = p(
    'Result.Page.V3.DesktopDrawerSearchResultFilters.Enabled',
  );

  const { filteredFlightsCount, flightsCount } = {
    ...searchData,
  };

  const handleResetAll = () => {
    setSearchData!({ filters: { ...resetAllFilters(currentSearchData?.filters) } });
  };

  const desktopFiltersComponent = (
    <DesktopFilters
      onClear={handleResetAll}
      onFilter={onFilter}
      selectedFilters={selectedFilters}
      setIsItineraryDrawerSelfTransferModalOpen={setIsItineraryDrawerSelfTransferModalOpen}
      sidebarRef={sidebarRef}
    />
  );

  const resultsSectionComponent = (
    <ResultsSection
      flightResultsWrapperRef={flightResultsWrapperRef}
      isNearbyDatesLoading={isNearbyDatesLoading}
      isSortingInformationEnabled={isSortingInformationEnabled}
      nearbyDatePrices={nearbyDatePrices}
      searchFormWatchRef={searchFormWatchRef}
      setFetchMoreResults={setFetchMoreResults}
      setValueRef={setValueRef}
    />
  );

  return (
    <ContentWrapper className={contentWrapperStyles}>
      {/* Case 1: Show SearchFilters Drawer */}
      {isResultPageV3DesktopDrawerSearchResultFiltersEnabled && (
        <>
          {quickSortVersion === 2 && (
            // count, clear and open drawer filters
            <Inline className={sortAndFilterControlsStyles} noWrap spacing={8}>
              <Inline
                className={desktopFilterControlsWhenSidebarDisabled}
                data-testid="resultPage-number-of-flights-shown"
              >
                <span>
                  {t(
                    'Result.SearchFilters.ShowingFilteredResults.Label',
                    filteredFlightsCount,
                    flightsCount,
                  )}
                </span>
                <LinkButton data-testid="clear-filters-link-button" onClick={handleResetAll}>
                  {t('Result.SearchFilters.ClearFilters.Button')}
                </LinkButton>
              </Inline>
              <ResultFiltersToggleButton />
            </Inline>
          )}
          <Stack>
            <div>
              {quickSortVersion === 2 && (
                // filters at drawer
                <div data-testid="resultPage-searchFilters" ref={sidebarRef}>
                  {desktopFiltersComponent}
                </div>
              )}
              <div data-testid="resultPage-searchResults">{resultsSectionComponent}</div>
            </div>
          </Stack>
        </>
      )}

      {/* Case 2: Show SearchFilters Sidebar */}
      {!isResultPageV3DesktopDrawerSearchResultFiltersEnabled && (
        <Stack>
          <Columns spacing={24}>
            {/* this column is filters at sidebar */}
            <Column data-testid="resultPage-searchFilters" ref={sidebarRef} width="3/12">
              {desktopFiltersComponent}
            </Column>
            <Column data-testid="resultPage-searchResults" width="9/12">
              {resultsSectionComponent}
            </Column>
          </Columns>
        </Stack>
      )}
    </ContentWrapper>
  );
};

export default SearchResultsDesktop;
